/* KnowledgeGraph — Three.js plexus network overlay.
 *
 * Adapted from the cinematic plexus pattern (BufferGeometry + custom shaders +
 * O(N²) connection pass) but retuned for our brand:
 *   - Transparent canvas (alpha:true) over the paper background
 *   - Ink color (dark) — NORMAL blending, not additive (additive on light = invisible)
 *   - Lower density appropriate for an overlay
 *   - Scroll-driven dissolve/reconstruct: nodes drift radially outward + global
 *     opacity ramps to zero as the user scrolls past the hero
 *
 * Three.js is bundled from npm (`three@0.128.0` matches the prior CDN r128).
 * No runtime CDN dependency, no SRI hash to maintain.
 */
import React from 'react';
// Three.js is bundled (`three@0.128.0`) but lazy-loaded via dynamic import
// so it stays out of the homepage's initial JS chunk. The decorative
// canvas is below the fold by every measure that matters.

function usePrefersReducedMotion() {
  const [reduce, setReduce] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );
  React.useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setReduce(mq.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return reduce;
}

// Production defaults. The `/kg-tweaks` page (Vite multi-entry) overrides
// these via the `config` prop while the user is dialing the look in.
// Values dialed in via /kg-tweaks on 2026-04-26.
const KG_DEFAULTS = {
  nodeCount: 260,
  maxDistance: 50,
  nodeSize: 4.2,
  pulseSpeed: 1.2,
  dof: 1.4,
  lineOpacity: 0.9,
  globalOpacity: 0.82,
};

const KnowledgeGraph = ({ config = null, onReady = null } = {}) => {
  // T5 — all hooks called unconditionally, before any early return, so the
  // hook count stays stable across re-renders when prefers-reduced-motion
  // toggles (DevTools > Rendering > Emulate CSS prefers-reduced-motion).
  const reduceMotion = usePrefersReducedMotion();
  const wrapRef = React.useRef(null);
  const stateRef = React.useRef({
    progress: 0,        // scroll dissolve 0→1
    pointerX: 0,        // -1..1 across viewport
    pointerY: 0,
    pointerActive: 0,   // 0 or 1
  });

  // Resolve effective config once per mount; param changes that require
  // re-init (nodeCount, maxDistance) come in via `key` prop on the parent.
  const cfg = { ...KG_DEFAULTS, ...(config || {}) };

  React.useEffect(() => {
    if (reduceMotion) return;
    let disposed = false;
    let cleanup = null;
    import('three').then((THREE) => {
      if (disposed || !wrapRef.current) return;
      cleanup = mountPlexus(THREE, wrapRef.current, stateRef, cfg, onReady);
    });
    return () => {
      disposed = true;
      if (cleanup) cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion]);

  // Scroll progress
  React.useEffect(() => {
    if (reduceMotion) return;
    const onScroll = () => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const start = 0;
      const end = -window.innerHeight * 0.7;
      const range = start - end;
      const p = Math.max(0, Math.min(1, (start - rect.top) / range));
      stateRef.current.progress = p;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [reduceMotion]);

  // Pointer parallax
  React.useEffect(() => {
    if (reduceMotion) return;
    const onMove = (e) => {
      stateRef.current.pointerX = (e.clientX / window.innerWidth) * 2 - 1;
      stateRef.current.pointerY = (e.clientY / window.innerHeight) * 2 - 1;
      stateRef.current.pointerActive = 1;
    };
    const onLeave = () => { stateRef.current.pointerActive = 0; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, [reduceMotion]);

  // Honour the OS reduced-motion preference; the canvas is decorative.
  if (reduceMotion) return null;

  return (
    <div
      ref={wrapRef}
      className="kg-plexus"
      aria-hidden="true"
      style={{
        // Reserve the canvas footprint up front so the dynamic three.js chunk
        // load can't trigger a layout shift on slow networks (CLS metric).
        // Anchored to the right edge so the dense particle center stays
        // right; canvas sized to 60vw so drift particles extend across
        // ~60% of the viewport width with sparser density toward the left.
        // Negative top lets the plexus spill visibly behind the sticky nav
        // (Nav z-index 50, KG z-index 0; Hero has overflow:visible).
        // Values dialed in via /kg-tweaks on 2026-04-26.
        position: 'absolute',
        right: '-4%',
        top: '-140px',
        width: 'min(840px, 60vw)',
        height: 'min(840px, 60vw)',
        minHeight: 'min(840px, 60vw)',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};

// ===========================================================================
// Three.js plexus mount
// ===========================================================================
function mountPlexus(THREE, container, stateRef, config, onReady) {
  // ---- shaders -------------------------------------------------------------
  const nodeVS = `
    uniform float u_size;
    uniform float u_dof;
    uniform float u_time;
    uniform float u_pulseSpeed;
    uniform float u_dissolve;

    attribute float a_base_size;
    attribute float a_alpha_mod;
    attribute float a_phase;
    attribute float a_tint;       // 0..1 mix toward warm color
    attribute vec3  a_outDir;     // radial direction for dissolve push

    varying float vDepthBlur;
    varying float vAlpha;
    varying float vTint;

    void main() {
      vec3 pos = position + a_outDir * (u_dissolve * 60.0);
      vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);

      float focalPlane = -110.0;
      float distFromFocus = abs(mvPosition.z - focalPlane);
      vDepthBlur = clamp(distFromFocus / 140.0, 0.0, 1.0);

      float baseScale = 240.0 / max(abs(mvPosition.z), 1.0);
      float blurExpansion = 1.0 + (vDepthBlur * u_dof * 3.0);
      float finalSize = a_base_size * u_size * baseScale * blurExpansion;

      float pulse = 0.85 + 0.15 * sin(u_time * u_pulseSpeed + a_phase);
      vAlpha = a_alpha_mod * pulse;
      vTint = a_tint;

      gl_PointSize = finalSize;
      gl_Position = projectionMatrix * mvPosition;
    }
  `;

  // Soft round point — feathered edge, no hard ring. Output as INK on light bg.
  const nodeFS = `
    uniform vec3  u_color;
    uniform vec3  u_warmColor;
    uniform float u_dof;
    uniform float u_globalOpacity;
    uniform float u_dissolve;

    varying float vDepthBlur;
    varying float vAlpha;
    varying float vTint;

    void main() {
      vec2 uv = gl_PointCoord - 0.5;
      float d = length(uv);

      // softer feather when blurred (DoF)
      float blurAmt = mix(0.06, 0.55, vDepthBlur * u_dof);

      // alpha falls off smoothly from center to edge — no ring
      float core = 1.0 - smoothstep(0.0, 0.5 - blurAmt * 0.4, d);
      float halo = 1.0 - smoothstep(0.0, 0.5, d);
      float alpha = mix(halo * 0.55, core, 1.0 - vDepthBlur);

      if (alpha <= 0.001) discard;

      // out-of-focus loses density
      float depthFade = 1.0 - vDepthBlur * 0.7;

      // Blend ink → warm tint per-node. Most nodes stay ink; a minority warm.
      vec3 col = mix(u_color, u_warmColor, vTint);

      float finalA = alpha * vAlpha * depthFade * u_globalOpacity * (1.0 - u_dissolve);
      gl_FragColor = vec4(col, finalA);
    }
  `;

  const lineVS = `
    attribute float a_alpha;
    uniform float u_dof;
    uniform float u_dissolve;
    attribute vec3 a_outDir;

    varying float vAlpha;
    varying float vDepthBlur;

    void main() {
      vec3 pos = position + a_outDir * (u_dissolve * 60.0);
      vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);

      float focalPlane = -110.0;
      float distFromFocus = abs(mvPosition.z - focalPlane);
      vDepthBlur = clamp(distFromFocus / 140.0, 0.0, 1.0);

      vAlpha = a_alpha;
      gl_Position = projectionMatrix * mvPosition;
    }
  `;

  const lineFS = `
    uniform vec3  u_color;
    uniform float u_lineOpacity;
    uniform float u_globalOpacity;
    uniform float u_dof;
    uniform float u_dissolve;

    varying float vAlpha;
    varying float vDepthBlur;

    void main() {
      // Lines fade FASTER than nodes during dissolve
      float dissolveFade = 1.0 - smoothstep(0.0, 0.6, u_dissolve);
      float depthFade = 1.0 - (vDepthBlur * u_dof * 0.8);
      float a = vAlpha * u_lineOpacity * depthFade * u_globalOpacity * dissolveFade;
      if (a <= 0.005) discard;
      gl_FragColor = vec4(u_color, a);
    }
  `;

  // ---- scene setup ---------------------------------------------------------
  const scene = new THREE.Scene();
  // no scene.background — keep canvas transparent

  const w = container.clientWidth;
  const h = container.clientHeight;

  const camera = new THREE.PerspectiveCamera(55, w / h, 0.1, 1000);
  camera.position.z = 0;

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
    premultipliedAlpha: true,
  });
  renderer.setSize(w, h, false);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0); // transparent
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  renderer.domElement.style.display = 'block';
  container.appendChild(renderer.domElement);

  const networkGroup = new THREE.Group();
  networkGroup.position.z = -150;
  scene.add(networkGroup);

  // ---- data structures -----------------------------------------------------
  const NODE_COUNT = config.nodeCount;
  const MAX_LINES = Math.max(1000, NODE_COUNT * 40);
  const bounds = { x: 220, y: 160, z: 200 };
  const MAX_DISTANCE = config.maxDistance;

  const nodePositions = new Float32Array(NODE_COUNT * 3);
  const nodeOutDirs   = new Float32Array(NODE_COUNT * 3);   // pre-baked radial direction
  const nodeVelocities = new Float32Array(NODE_COUNT * 3);
  const nodeBaseSizes = new Float32Array(NODE_COUNT);
  const nodeAlphas    = new Float32Array(NODE_COUNT);
  const nodePhases    = new Float32Array(NODE_COUNT);
  const nodeTints     = new Float32Array(NODE_COUNT);

  for (let i = 0; i < NODE_COUNT; i++) {
    const px = (Math.random() - 0.5) * bounds.x;
    const py = (Math.random() - 0.5) * bounds.y;
    const pz = -(Math.random() * bounds.z + 10);
    nodePositions[i*3]   = px;
    nodePositions[i*3+1] = py;
    nodePositions[i*3+2] = pz;

    // outward direction = unit vector from center
    const len = Math.max(0.001, Math.hypot(px, py, pz + bounds.z * 0.5));
    nodeOutDirs[i*3]   = px / len;
    nodeOutDirs[i*3+1] = py / len;
    nodeOutDirs[i*3+2] = (pz + bounds.z * 0.5) / len * 0.4; // less Z dispersal

    nodeVelocities[i*3]   = (Math.random() - 0.5) * 0.12;
    nodeVelocities[i*3+1] = (Math.random() - 0.5) * 0.12;
    nodeVelocities[i*3+2] = (Math.random() - 0.5) * 0.12;

    // Cubic distribution biases towards smaller nodes; a handful of larger "hero" nodes
    const r = Math.random();
    nodeBaseSizes[i] = Math.pow(r, 2.4) * 1.9 + 0.55;
    if (i < 6) nodeBaseSizes[i] = 2.4 + Math.random() * 0.8; // a few prominent ones
    nodeAlphas[i]    = 0.65 + Math.random() * 0.35;
    nodePhases[i]    = Math.random() * Math.PI * 2;

    // ~25% of nodes get warm tint (clay/ochre); the rest stay ink.
    // Larger nodes are slightly more likely to be warm so they read as accents.
    const warmChance = 0.20 + (nodeBaseSizes[i] / 3.5) * 0.25;
    nodeTints[i] = Math.random() < warmChance ? (0.6 + Math.random() * 0.4) : 0;
  }

  const linePositions = new Float32Array(MAX_LINES * 6);
  const lineAlphas    = new Float32Array(MAX_LINES * 2);
  const lineOutDirs   = new Float32Array(MAX_LINES * 6);

  // ---- geometries & materials ----------------------------------------------
  const nodeGeometry = new THREE.BufferGeometry();
  nodeGeometry.setAttribute('position',   new THREE.BufferAttribute(nodePositions,  3).setUsage(THREE.DynamicDrawUsage));
  nodeGeometry.setAttribute('a_base_size',new THREE.BufferAttribute(nodeBaseSizes,  1));
  nodeGeometry.setAttribute('a_alpha_mod',new THREE.BufferAttribute(nodeAlphas,     1));
  nodeGeometry.setAttribute('a_phase',    new THREE.BufferAttribute(nodePhases,     1));
  nodeGeometry.setAttribute('a_tint',     new THREE.BufferAttribute(nodeTints,      1));
  nodeGeometry.setAttribute('a_outDir',   new THREE.BufferAttribute(nodeOutDirs,    3));

  const lineGeometry = new THREE.BufferGeometry();
  lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3).setUsage(THREE.DynamicDrawUsage));
  lineGeometry.setAttribute('a_alpha',  new THREE.BufferAttribute(lineAlphas,    1).setUsage(THREE.DynamicDrawUsage));
  lineGeometry.setAttribute('a_outDir', new THREE.BufferAttribute(lineOutDirs,   3).setUsage(THREE.DynamicDrawUsage));

  // Brand colors — read from CSS custom properties if available
  const inkColor  = readCssColor('--ink')  || '#2B211A';
  // Warm color = clay deep (slightly muted from full clay so it doesn't shout)
  const warmColor = readCssColor('--clay-deep') || '#A64828';

  const nodeUniforms = {
    u_time:          { value: 0 },
    u_size:          { value: config.nodeSize },
    u_dof:           { value: config.dof },
    u_pulseSpeed:    { value: config.pulseSpeed },
    u_globalOpacity: { value: config.globalOpacity },
    u_dissolve:      { value: 0 },
    u_color:         { value: new THREE.Color(inkColor) },
    u_warmColor:     { value: new THREE.Color(warmColor) },
  };
  const lineUniforms = {
    // WebGL native lines are stuck at 1px on most GPUs, so opacity does the
    // visual work for line thickness perception.
    u_lineOpacity:   { value: config.lineOpacity },
    u_globalOpacity: { value: config.globalOpacity },
    u_dof:           { value: config.dof },
    u_dissolve:      { value: 0 },
    u_color:         { value: new THREE.Color(inkColor) },
  };

  const nodeMaterial = new THREE.ShaderMaterial({
    vertexShader: nodeVS,
    fragmentShader: nodeFS,
    uniforms: nodeUniforms,
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending, // ink on light paper, NOT additive
  });
  const lineMaterial = new THREE.ShaderMaterial({
    vertexShader: lineVS,
    fragmentShader: lineFS,
    uniforms: lineUniforms,
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
  });

  const nodes = new THREE.Points(nodeGeometry, nodeMaterial);
  const lines = new THREE.LineSegments(lineGeometry, lineMaterial);
  networkGroup.add(nodes);
  networkGroup.add(lines);

  // Expose live-mutable uniforms for the /kg-tweaks page. Production
  // never passes onReady, so this is a no-op in the home build.
  onReady?.({ nodeUniforms, lineUniforms });

  // ---- resize --------------------------------------------------------------
  const onResize = () => {
    const ww = container.clientWidth;
    const hh = container.clientHeight;
    if (!ww || !hh) return;
    renderer.setSize(ww, hh, false);
    camera.aspect = ww / hh;
    camera.updateProjectionMatrix();
  };
  window.addEventListener('resize', onResize);

  // ---- animation loop ------------------------------------------------------
  const clock = new THREE.Clock();
  let raf = 0;

  function animate() {
    raf = requestAnimationFrame(animate);
    const t = clock.getElapsedTime();
    const st = stateRef.current;
    const dissolve = easeOut(st.progress);

    nodeUniforms.u_time.value = t;
    nodeUniforms.u_dissolve.value = dissolve;
    lineUniforms.u_dissolve.value = dissolve;

    // Slow Y spin + gentle pointer-driven X/Y tilt for parallax
    networkGroup.rotation.y += 0.0008;
    const targetTiltX = st.pointerY * 0.25 * st.pointerActive;
    const targetTiltY = -st.pointerX * 0.20 * st.pointerActive;
    networkGroup.rotation.x += (targetTiltX - networkGroup.rotation.x) * 0.04;
    // additive on top of base spin — apply tilt to a wrapper instead? simpler: lerp directly into rotation.x
    // For Y spin we let it accumulate; small offset is fine
    networkGroup.rotation.z += (targetTiltY - networkGroup.rotation.z) * 0.04;

    // Drift physics (no global drift, just per-node random vel)
    const pos = nodeGeometry.attributes.position.array;
    for (let i = 0; i < NODE_COUNT; i++) {
      const ix = i * 3;
      pos[ix]   += nodeVelocities[ix];
      pos[ix+1] += nodeVelocities[ix+1];
      pos[ix+2] += nodeVelocities[ix+2];

      // Wrap inside bounds box
      if (pos[ix]   >  bounds.x/2) pos[ix]   = -bounds.x/2;
      if (pos[ix]   < -bounds.x/2) pos[ix]   =  bounds.x/2;
      if (pos[ix+1] >  bounds.y/2) pos[ix+1] = -bounds.y/2;
      if (pos[ix+1] < -bounds.y/2) pos[ix+1] =  bounds.y/2;
      if (pos[ix+2] >  0)          pos[ix+2] = -bounds.z;
      if (pos[ix+2] < -bounds.z)   pos[ix+2] =  0;
    }

    // Connections — O(N²)
    const maxDistSq = MAX_DISTANCE * MAX_DISTANCE;
    const lp = lineGeometry.attributes.position.array;
    const la = lineGeometry.attributes.a_alpha.array;
    const lo = lineGeometry.attributes.a_outDir.array;
    let lineIdx = 0;

    for (let i = 0; i < NODE_COUNT; i++) {
      const ix = i * 3;
      for (let j = i + 1; j < NODE_COUNT; j++) {
        const jx = j * 3;
        const dx = pos[ix]   - pos[jx];
        const dy = pos[ix+1] - pos[jx+1];
        const dz = pos[ix+2] - pos[jx+2];
        const distSq = dx*dx + dy*dy + dz*dz;
        if (distSq < maxDistSq && lineIdx < MAX_LINES) {
          const dist = Math.sqrt(distSq);
          const alpha = 1 - dist / MAX_DISTANCE;
          const li6 = lineIdx * 6;
          const li2 = lineIdx * 2;

          lp[li6]   = pos[ix];   lp[li6+1] = pos[ix+1]; lp[li6+2] = pos[ix+2];
          lp[li6+3] = pos[jx];   lp[li6+4] = pos[jx+1]; lp[li6+5] = pos[jx+2];
          la[li2]   = alpha;     la[li2+1] = alpha;
          lo[li6]   = nodeOutDirs[ix];   lo[li6+1] = nodeOutDirs[ix+1]; lo[li6+2] = nodeOutDirs[ix+2];
          lo[li6+3] = nodeOutDirs[jx];   lo[li6+4] = nodeOutDirs[jx+1]; lo[li6+5] = nodeOutDirs[jx+2];
          lineIdx++;
        }
      }
    }

    nodeGeometry.attributes.position.needsUpdate = true;
    lineGeometry.setDrawRange(0, lineIdx * 2);
    lineGeometry.attributes.position.needsUpdate = true;
    lineGeometry.attributes.a_alpha.needsUpdate = true;
    lineGeometry.attributes.a_outDir.needsUpdate = true;

    renderer.render(scene, camera);
  }
  animate();

  // ---- cleanup -------------------------------------------------------------
  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', onResize);
    nodeGeometry.dispose();
    lineGeometry.dispose();
    nodeMaterial.dispose();
    lineMaterial.dispose();
    renderer.dispose();
    if (renderer.domElement.parentNode === container) {
      container.removeChild(renderer.domElement);
    }
  };
}

function easeOut(v) { return 1 - Math.pow(1 - v, 3); }

function readCssColor(name) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || null;
  } catch { return null; }
}

export default KnowledgeGraph;
